import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { BarChart3, TrendingUp, AlertTriangle, DollarSign, PlugZap, Gauge, PhoneForwarded, Phone, HardDrive } from 'lucide-react';
import { DEFAULT_FX_USD_TO_MXN, costPerAiVoiceMinute, costPerLinkedMinute } from '@/lib/planCostSimulator';

const VENDOR_LINK_MODE_LABELS = { ivr: 'IVR (presiona 1)', bridge: 'Bridge (conferencia)', unknown: 'Sin modo' };
const EXAMPLE_FX = DEFAULT_FX_USD_TO_MXN; // MXN per 1 USD (solo para convertir MXN→USD)
const EXAMPLE_AI_MIN = costPerAiVoiceMinute();
const EXAMPLE_LINK_IVR = costPerLinkedMinute('ivr');
const EXAMPLE_LINK_BRIDGE = costPerLinkedMinute('bridge');

/** Convierte un monto a USD reales. USD se deja; MXN se divide por el tipo de cambio. */
function toUsd(amount, currency = 'USD', fx = EXAMPLE_FX) {
  const n = Number(amount) || 0;
  const cur = (currency || 'USD').toUpperCase();
  if (cur === 'USD') return n;
  if (cur === 'MXN') return fx > 0 ? n / fx : n;
  return n;
}

function sumToUsd(byCurrencyMap, fx = EXAMPLE_FX) {
  return Object.entries(byCurrencyMap || {}).reduce((s, [cur, amt]) => s + toUsd(amt, cur, fx), 0);
}

const MODULE_LABELS = { ai: 'IA', voice: 'Voz (telefonía)', whatsapp: 'WhatsApp', email: 'Email', crm: 'CRM', marketing: 'Marketing' };
// Sprint 36: distinguishes Twilio's real per-call telephony cost from the
// estimated realtime-voice-AI audio cost within the same 'voice' module —
// without this, both would render under one identical "Voz (telefonía)" label.
const COST_TYPE_LABELS = { realtime_ai_audio: 'IA de voz en tiempo real (estimado)' };

export default function UsageDashboard() {
  const [usage, setUsage] = useState(null);
  const [costs, setCosts] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [profitability, setProfitability] = useState([]);
  const [failedIntegrations, setFailedIntegrations] = useState([]);
  const [nearLimit, setNearLimit] = useState([]);
  const [storageUsage, setStorageUsage] = useState([]);
  const [multiInstance, setMultiInstance] = useState([]);
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [uRes, cRes, aRes, pRes, fiRes, nlRes, suRes, miRes] = await Promise.all([
        api.get(`/admin/usage?period=${period}`),
        api.get(`/admin/costs?period=${period}`),
        api.get('/admin/alerts?limit=20'),
        api.get('/admin/profitability').catch(() => ({ data: { tenants: [] } })),
        api.get('/admin/integrations/failed').catch(() => ({ data: [] })),
        api.get('/admin/tenants-near-limit').catch(() => ({ data: [] })),
        api.get('/admin/storage/usage').catch(() => ({ data: { tenants: [] } })),
        api.get('/admin/integration-purposes/health').catch(() => ({ data: { multi_instance_tenants: [] } })),
      ]);
      setUsage(uRes.data);
      setCosts(cRes.data);
      setAlerts(aRes.data);
      setProfitability(pRes.data.tenants || []);
      setFailedIntegrations(fiRes.data || []);
      setNearLimit(nlRes.data || []);
      setStorageUsage(suRes.data.tenants || []);
      setMultiInstance(miRes.data.multi_instance_tenants || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [period]);

  if (loading) return <PantallaCargando />;

  const usageTypes = usage?.by_type || {};
  const costModulesByCurrency = costs?.by_module_currency || [];
  const apiHasVendorLink = costs != null && Object.prototype.hasOwnProperty.call(costs, 'vendor_link');
  const vendorLink = costs?.vendor_link || null;
  const vendorLinkCosts = Object.entries(vendorLink?.total_by_currency || {});
  const hasVendorLinkActivity = (vendorLink?.call_legs || 0) > 0 || vendorLinkCosts.length > 0;

  return (
    <div data-testid="usage-dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Consumo & Costos</h1>
          <p className="text-sm text-[#64748B] mt-1">Costos en USD reales (MXN se convierte a 1 USD = {EXAMPLE_FX} MXN)</p>
        </div>
        <div className="flex gap-1 bg-white border border-[#E2E8F0] rounded-lg p-0.5">
          {['daily', 'monthly', 'yearly'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`}
              className={`px-3 py-1.5 text-xs rounded-md transition-all ${period === p ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:text-[#0F172A]'}`}>
              {p === 'daily' ? 'Hoy' : p === 'monthly' ? 'Mes' : 'Año'}
            </button>
          ))}
        </div>
      </div>

      {/* Referencia de precios de ejemplo (siempre visible) */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 mb-4" data-testid="voice-cost-reference">
        <h3 className="text-sm font-semibold text-[#0F172A] mb-1">Referencia: costo por minuto (USD)</h3>
        <p className="text-[11px] text-[#94A3B8] mb-3">
          Twilio ≈ $0.03 USD/min por pierna · OpenAI Realtime ≈ $0.15 USD/min. Panel completo en USD.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-[10px] text-[#94A3B8] uppercase">1 min voz con IA</p>
            <p className="text-lg font-bold text-[#0F172A]">${EXAMPLE_AI_MIN.usd.toFixed(3)} <span className="text-sm font-normal text-[#94A3B8]">USD</span></p>
            <p className="text-[11px] text-[#64748B]">Twilio + Realtime</p>
          </div>
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-[10px] text-[#94A3B8] uppercase">1 min enlace IVR</p>
            <p className="text-lg font-bold text-[#0F172A]">${EXAMPLE_LINK_IVR.usd.toFixed(3)} <span className="text-sm font-normal text-[#94A3B8]">USD</span></p>
            <p className="text-[11px] text-[#64748B]">Solo Twilio, 1 pierna</p>
          </div>
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-[10px] text-[#94A3B8] uppercase">1 min enlace bridge</p>
            <p className="text-lg font-bold text-[#0F172A]">${EXAMPLE_LINK_BRIDGE.usd.toFixed(3)} <span className="text-sm font-normal text-[#94A3B8]">USD</span></p>
            <p className="text-[11px] text-[#64748B]">2 piernas Twilio</p>
          </div>
        </div>
      </div>

      {/* Llamadas enlazadas — arriba del fold, siempre visible */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="vendor-link-panel">
        <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-1">
          <PhoneForwarded className="w-4 h-4 text-[#64748B]" /> Costos de Llamadas Enlazadas
        </h3>
        <p className="text-[11px] text-[#94A3B8] mb-4">
          Enlace lead–vendedor (IVR / bridge). Solo minutos de telefonía Twilio; no consume IA de voz por minuto.
        </p>
        {!apiHasVendorLink ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="vendor-link-api-missing">
            El API aún no devuelve el desglose <code className="text-[11px]">vendor_link</code>. Hay que desplegar el backend actualizado (endpoint <code className="text-[11px]">GET /api/admin/costs</code>).
          </p>
        ) : !hasVendorLinkActivity ? (
          <p className="text-xs text-[#94A3B8] py-2 text-center" data-testid="vendor-link-empty">Sin llamadas enlazadas en este período</p>
        ) : (
          <div className="space-y-5" data-testid="vendor-link-costs">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-[#F8FAFC] rounded-lg p-3">
                <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide">Piernas</p>
                <p className="text-lg font-bold text-[#0F172A]">{vendorLink.call_legs}</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-3">
                <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide">Completadas</p>
                <p className="text-lg font-bold text-[#0F172A]">{vendorLink.completed_legs}</p>
              </div>
              <div className="bg-[#F8FAFC] rounded-lg p-3">
                <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide">Minutos (duración)</p>
                <p className="text-lg font-bold text-[#0F172A]">{(vendorLink.duration_minutes || 0).toFixed(1)}</p>
              </div>
                <div className="bg-[#F8FAFC] rounded-lg p-3">
                  <p className="text-[10px] text-[#94A3B8] uppercase tracking-wide">Costo facturado (USD)</p>
                  <p className="text-lg font-bold text-[#0F172A]">
                    ${sumToUsd(vendorLink?.total_by_currency).toFixed(2)} <span className="text-xs font-normal text-[#94A3B8]">USD</span>
                  </p>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className="text-xs font-semibold text-[#0F172A] mb-2">Por modo</h4>
                {(vendorLink.by_mode || []).length === 0 ? (
                  <p className="text-[11px] text-[#94A3B8]">Sin desglose</p>
                ) : (
                  <div className="space-y-1.5">
                    {vendorLink.by_mode.map((row) => (
                      <div key={row.mode} className="flex items-center justify-between">
                        <span className="text-xs text-[#475569]">{VENDOR_LINK_MODE_LABELS[row.mode] || row.mode}</span>
                        <span className="text-xs font-medium text-[#0F172A]">
                          {row.call_legs} piernas · {Number(row.duration_minutes || 0).toFixed(1)} min
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-xs font-semibold text-[#0F172A] mb-2">Por proveedor</h4>
                {(vendorLink.by_provider || []).length === 0 ? (
                  <p className="text-[11px] text-[#94A3B8]">Sin costo Twilio aún</p>
                ) : (
                  <div className="space-y-1.5">
                    {vendorLink.by_provider.slice().sort((a, b) => b.total - a.total).map((row, i) => (
                      <div key={`${row.provider}-${row.currency}-${i}`} className="flex items-center justify-between">
                        <span className="text-xs text-[#475569] capitalize">{(row.provider || '').replace(/_/g, ' ')}</span>
                        <span className="text-xs font-medium text-[#0F172A]">
                          ${toUsd(row.total, row.currency).toFixed(2)} USD
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <h4 className="text-xs font-semibold text-[#0F172A] mb-2">Por tenant</h4>
                {(vendorLink.by_tenant || []).length === 0 ? (
                  <p className="text-[11px] text-[#94A3B8]">Sin tenants</p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {vendorLink.by_tenant.map((row) => (
                      <div key={row.tenant_id} className="flex items-center justify-between gap-2 py-0.5 border-b border-[#F8F9FA] last:border-0">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-[#0F172A] truncate">{row.tenant_name}</p>
                          <p className="text-[11px] text-[#94A3B8]">{row.call_legs} piernas · {Number(row.duration_minutes || 0).toFixed(1)} min</p>
                        </div>
                        <div className="text-right shrink-0">
                          {(row.costs || []).length === 0 ? (
                            <span className="text-xs text-[#94A3B8]">—</span>
                          ) : (
                            row.costs.map((c) => (
                              <p key={c.currency} className="text-xs font-medium text-[#0F172A]">${toUsd(c.total, c.currency).toFixed(2)} USD</p>
                            ))
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {vendorLink.note && <p className="text-[10px] text-[#94A3B8]">{vendorLink.note}</p>}
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><BarChart3 className="w-4 h-4 text-[#64748B]" /><span className="text-xs text-[#94A3B8]">Eventos Totales</span></div>
          <p className="text-2xl font-bold text-[#0F172A]" data-testid="total-events">{usage?.total_events || 0}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><DollarSign className="w-4 h-4 text-[#64748B]" /><span className="text-xs text-[#94A3B8]">Costo Total (USD)</span></div>
          <p className="text-2xl font-bold text-[#0F172A]" data-testid="total-cost">
            ${sumToUsd(costs?.total_by_currency).toFixed(2)} <span className="text-sm font-normal text-[#94A3B8]">USD</span>
          </p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-[#64748B]" /><span className="text-xs text-[#94A3B8]">Tipos de Evento</span></div>
          <p className="text-2xl font-bold text-[#0F172A]">{Object.keys(usageTypes).length}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4 text-amber-500" /><span className="text-xs text-[#94A3B8]">Alertas Activas</span></div>
          <p className="text-2xl font-bold text-[#0F172A]" data-testid="active-alerts">{alerts.filter(a => a.status === 'triggered').length}</p>
        </div>
      </div>

      {/* Voice minutes — inbound + outbound talk time and blended $/min */}
      {(() => {
        const vm = costs?.voice_minutes;
        if (!vm) return null;
        const primary = vm.primary || (vm.totals_by_currency && vm.totals_by_currency[0]) || null;
        const tel = vm.by_cost_type?.telephony;
        const aiVoice = vm.by_cost_type?.realtime_ai_audio;
        const inbound = vm.by_direction?.inbound;
        const outbound = vm.by_direction?.outbound;
        return (
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="voice-minutes-card">
            <div className="flex items-center gap-2 mb-3">
              <Phone className="w-4 h-4 text-[#0F172A]" />
              <h3 className="text-sm font-semibold text-[#0F172A]">Minutos de llamadas (entrada + salida)</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-[11px] text-[#94A3B8] mb-1">Minutos consumidos</p>
                <p className="text-2xl font-bold text-[#0F172A]" data-testid="voice-minutes-consumed">
                  {Number(vm.minutes_consumed || 0).toFixed(1)}
                </p>
                <p className="text-[10px] text-[#94A3B8] mt-0.5">
                  {vm.call_count || 0} llamadas completadas
                  {(inbound || outbound) && (
                    <span>
                      {' '}· in {Number(inbound?.minutes || 0).toFixed(1)} / out {Number(outbound?.minutes || 0).toFixed(1)}
                    </span>
                  )}
                </p>
              </div>
              <div>
                <p className="text-[11px] text-[#94A3B8] mb-1">Costo integrado por minuto</p>
                {primary ? (
                  <>
                    <p className="text-2xl font-bold text-[#0F172A]" data-testid="voice-cost-per-minute">
                      ${Number(primary.avg_cost_per_minute || 0).toFixed(4)}
                      <span className="text-sm font-normal text-[#94A3B8] ml-1">{primary.currency}</span>
                    </p>
                    <p className="text-[10px] text-[#94A3B8] mt-0.5">
                      Promedio ponderado (telefonía + IA de voz del período)
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-[#94A3B8]">Sin tarifa en este período</p>
                )}
              </div>
              <div>
                <p className="text-[11px] text-[#94A3B8] mb-1">Costo total de minutos</p>
                {primary || (vm.totals_by_currency || []).length > 0 ? (
                  <div data-testid="voice-minutes-total-cost">
                    {(vm.totals_by_currency || []).map((row) => (
                      <p key={row.currency} className="text-2xl font-bold text-[#0F172A]">
                        ${Number(row.total_cost || 0).toFixed(2)}
                        <span className="text-sm font-normal text-[#94A3B8] ml-1">{row.currency}</span>
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-2xl font-bold text-[#0F172A]">$0.00</p>
                )}
              </div>
            </div>
            {(tel || aiVoice) && (
              <div className="mt-4 pt-3 border-t border-[#F1F5F9] grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px] text-[#64748B]">
                {tel && (
                  <p>
                    Telefonía (Twilio real): {Number(tel.minutes).toFixed(1)} min · ${Number(tel.avg_cost_per_minute).toFixed(4)}/min · ${Number(tel.total_cost).toFixed(2)} {tel.currency}
                  </p>
                )}
                {aiVoice && (
                  <p>
                    IA de voz (estimado): {Number(aiVoice.minutes).toFixed(1)} min · ${Number(aiVoice.avg_cost_per_minute).toFixed(4)}/min · ${Number(aiVoice.total_cost).toFixed(2)} {aiVoice.currency}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })()}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage by type */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Consumo por Tipo</h3>
          {Object.keys(usageTypes).length === 0 ? (
            <p className="text-xs text-[#94A3B8] py-4 text-center">Sin eventos en este período</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(usageTypes).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-xs text-[#475569]">{type.replace(/_/g, ' ')}</span>
                  <span className="text-xs font-medium text-[#0F172A] bg-[#F1F5F9] px-2 py-0.5 rounded">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Costs by module */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Costos por Módulo</h3>
          {costModulesByCurrency.length === 0 ? (
            <p className="text-xs text-[#94A3B8] py-4 text-center">Sin costos en este período</p>
          ) : (
            <div className="space-y-2">
              {costModulesByCurrency.slice().sort((a, b) => b.total - a.total).map((row, i) => (
                <div key={`${row.module}-${row.cost_type}-${row.currency}-${i}`} className="flex items-center justify-between">
                  <span className="text-xs text-[#475569]">
                    {MODULE_LABELS[row.module] || row.module}
                    {COST_TYPE_LABELS[row.cost_type] && (
                      <span className="text-[#94A3B8]"> — {COST_TYPE_LABELS[row.cost_type]}</span>
                    )}
                  </span>
                  <span className="text-xs font-medium text-[#0F172A]">${toUsd(row.total, row.currency).toFixed(2)} USD</span>
                </div>
              ))}
              <p className="text-[10px] text-[#94A3B8] pt-1">Todo convertido a USD. Si el evento venía en MXN se usa 1 USD = {EXAMPLE_FX} MXN.</p>
            </div>
          )}
        </div>

        {/* S42(2/N) — Costs by real provider */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Costos por Proveedor</h3>
          {(costs?.by_provider || []).length === 0 ? (
            <p className="text-xs text-[#94A3B8] py-4 text-center">Sin costos con proveedor asignado en este período</p>
          ) : (
            <div className="space-y-2" data-testid="costs-by-provider">
              {costs.by_provider.slice().sort((a, b) => b.total - a.total).map((row, i) => (
                <div key={`${row.provider}-${row.currency}-${i}`} className="flex items-center justify-between">
                  <span className="text-xs text-[#475569] capitalize">{row.provider.replace(/_/g, ' ')}</span>
                  <span className="text-xs font-medium text-[#0F172A]">${toUsd(row.total, row.currency).toFixed(2)} USD</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* S42(2/N) — Integrations with errors, across all tenants */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><PlugZap className="w-4 h-4 text-red-500" /> Integraciones con Errores</h3>
          {failedIntegrations.length === 0 ? (
            <p className="text-xs text-[#94A3B8] py-4 text-center">Ninguna integración con errores</p>
          ) : (
            <div className="space-y-2" data-testid="failed-integrations">
              {failedIntegrations.map(fi => (
                <div key={fi.integration_id} className="flex items-center justify-between py-1.5 border-b border-[#F8F9FA] last:border-0">
                  <div>
                    <p className="text-xs font-medium text-[#0F172A]">{fi.tenant_name} — {fi.provider_name}</p>
                    {fi.last_test_message && <p className="text-[11px] text-[#94A3B8] truncate max-w-xs">{fi.last_test_message}</p>}
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 shrink-0">{fi.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        {/* S42(2/N) — Tenants near their plan limit (reuses UsageAlert) */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><Gauge className="w-4 h-4 text-amber-500" /> Tenants Cerca de su Límite</h3>
          {nearLimit.length === 0 ? (
            <p className="text-xs text-[#94A3B8] py-4 text-center">Ningún tenant sobre el 75% de su límite</p>
          ) : (
            <div className="space-y-2" data-testid="tenants-near-limit">
              {nearLimit.map((n, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F8F9FA] last:border-0">
                  <div>
                    <p className="text-xs font-medium text-[#0F172A]">{n.tenant_name}</p>
                    <p className="text-[11px] text-[#94A3B8]">{n.limit_key.replace(/_/g, ' ')}: {n.current_usage}/{n.limit_value}</p>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${n.threshold >= 100 ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}>{n.threshold}%</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* S42(2/N) — Profitability per tenant (plan + addons revenue vs real cost) */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-1">Rentabilidad por Tenant</h3>
          <p className="text-[11px] text-[#94A3B8] mb-3">Ingreso del plan + addons activos, menos el costo real de este mes.</p>
          {profitability.length === 0 ? (
            <p className="text-xs text-[#94A3B8] py-4 text-center">Sin suscripciones activas</p>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="profitability-table">
              {profitability.map(p => (
                <div key={p.tenant_id} className="flex items-center justify-between py-1.5 border-b border-[#F8F9FA] last:border-0">
                  <div>
                    <p className="text-xs font-medium text-[#0F172A]">{p.tenant_name}</p>
                    <p className="text-[11px] text-[#94A3B8]">{p.plan_name || 'Sin plan'} · ${toUsd(p.revenue_monthly, p.currency).toFixed(2)} ingreso − ${toUsd(p.cost_this_month, p.currency === 'MXN' ? 'MXN' : 'USD').toFixed(2)} costo (USD)</p>
                  </div>
                  <span className={`text-xs font-semibold shrink-0 ${p.profitability < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {p.profitability < 0 ? '-' : ''}${Math.abs(toUsd(p.profitability, p.currency)).toFixed(2)} USD
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Almacenamiento por tenant — antes de esta pantalla, el sistema de
          cuotas existía en el backend pero no se conectaba a ninguna subida
          real ni tenía panel visible; ahora refleja archivos de Conocimiento
          y Publicaciones subidos desde que ese contador se activó. */}
      <div className="mt-6 bg-white border border-[#E2E8F0] rounded-xl p-5">
        <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-1"><HardDrive className="w-4 h-4" /> Almacenamiento por Tenant</h3>
        <p className="text-[11px] text-[#94A3B8] mb-3">Documentos de Conocimiento y medios de Publicaciones. Solo cuenta lo subido desde que este contador se activó — no incluye archivos anteriores.</p>
        {storageUsage.length === 0 ? (
          <p className="text-xs text-[#94A3B8] py-4 text-center">Sin uso de almacenamiento registrado todavía</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="storage-usage-table">
            {storageUsage.map(s => (
              <div key={s.tenant_id} className="flex items-center justify-between py-1.5 border-b border-[#F8F9FA] last:border-0">
                <div>
                  <p className="text-xs font-medium text-[#0F172A]">{s.tenant_name}</p>
                  <p className="text-[11px] text-[#94A3B8]">{(s.used_bytes / (1024 ** 2)).toFixed(1)} MB / {(s.limit_bytes / (1024 ** 3)).toFixed(0)} GB</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${s.usage_percent >= 100 ? 'bg-red-50 text-red-600' : s.usage_percent >= 75 ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>{s.usage_percent}%</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Adopción multi-instancia — Fase 2 (07-18) construyó este dato
          (multi_instance_tenants) pero nunca ganó una pantalla; se
          recuperó al auditar integration_purposes.py como parte de la
          limpieza de código muerto (2026-07-24). */}
      {multiInstance.length > 0 && (
        <div className="mt-6 bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-1"><PlugZap className="w-4 h-4" /> Adopción multi-instancia</h3>
          <p className="text-[11px] text-[#94A3B8] mb-3">Tenants con 2 o más conexiones activas del mismo proveedor (ej. varios números WhatsApp).</p>
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="multi-instance-table">
            {multiInstance.map(m => (
              <div key={`${m.tenant_id}-${m.provider_key}`} className="flex items-center justify-between py-1.5 border-b border-[#F8F9FA] last:border-0">
                <p className="text-xs font-medium text-[#0F172A]">{m.tenant_name}</p>
                <span className="text-[11px] px-2 py-0.5 rounded-full shrink-0 bg-violet-50 text-violet-700">{m.provider_key} · {m.connections}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="mt-6 bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Alertas Recientes</h3>
          <div className="space-y-2">
            {alerts.map(a => (
              <div key={a.id} className="flex items-center justify-between py-2 border-b border-[#F8F9FA] last:border-0" data-testid={`alert-${a.id}`}>
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`w-4 h-4 ${a.threshold >= 100 ? 'text-red-500' : a.threshold >= 90 ? 'text-amber-500' : 'text-yellow-400'}`} />
                  <div>
                    <p className="text-xs font-medium text-[#0F172A]">{a.limit_key.replace(/_/g, ' ')}</p>
                    <p className="text-[11px] text-[#94A3B8]">{a.current_usage}/{a.limit_value} ({a.threshold}%)</p>
                  </div>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${a.status === 'triggered' ? 'bg-red-50 text-red-600' : 'bg-[#F1F5F9] text-[#94A3B8]'}`}>{a.status === 'triggered' ? 'Activada' : 'Reconocida'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
